import type { ChangeEvent } from "react";
import React from "react";
import {
    Box,
    Modal,
    Checkbox,
    CheckboxGroup,
    Flex,
    RadioGroup,
    RadioButton,
    Icon,
} from "@proda-ai/ui-kit";
import Typography from "@/ui/atoms/Typography";
import type { TAnnualizationType, TExtractionType } from "@/apis/odin/apiODIN";
import WarningMessage from "@/ui/atoms/WarningMessage";
import { assertNever } from "@/utils/guards";

const canExportExtraction = (t: TExtractionType): boolean => {
    switch (t) {
        case "operating_statement_actual_to_budget":
        case "operating_statement_monthly_actual":
        case "operating_statement_monthly_budget":
        case "operating_statement_yearly_actual":
        case "operating_statement_yearly_budget":
        case "year_to_date":
            return true;
        case "rent_roll_extraction":
        case "other":
            return false;
        default:
            return assertNever(t);
    }
};

const canCompatExportExtraction = (t: TExtractionType): boolean => {
    switch (t) {
        case "operating_statement_monthly_actual":
        case "operating_statement_monthly_budget":
            return true;
        case "operating_statement_actual_to_budget":
        case "operating_statement_yearly_actual":
        case "operating_statement_yearly_budget":
        case "year_to_date":
        case "rent_roll_extraction":
        case "other":
            return false;
        default:
            return assertNever(t);
    }
};
const canCompatExportAnnualizationType = (t: TAnnualizationType): boolean => {
    switch (t) {
        case "monthly_actual":
        case "monthly_budget":
            return true;
        case "yearly_actual":
        case "yearly_budget":
            return false;
        default:
            return assertNever(t);
    }
};

export const testIds = {
    modal: "ViewExportModal-modal",
    detailedSummaryCheckbox: "ViewExportModal-detailedSummaryCheckbox",
    summaryCheckbox: "ViewExportModal-summaryCheckbox",
    mappingsCheckbox: "ViewExportModal-mappingsCheckbox",
    nonOperatingCheckbox: "ViewExportModal-nonOperatingCheckbox",
    prodaRadioButton: "ViewExportModal-prodaRadioButton",
    compatRadioButton: "ViewExportModal-compatRadioButton",
};

type TExportConfig = {
    exportFormat: "proda" | "compat";
    prodaIncludeDetailedSummary: boolean;
    prodaIncludeSummary: boolean;
    prodaIncludeMappings: boolean;
    includeNonOperating: boolean;
};
type TExportData =
    | {
          tag: "extractions";
          extractions: {
              extractionId: number;
              extractionType: TExtractionType;
          }[];
      }
    | {
          tag: "annualization";
          projectId: number;
          startDate: string;
          endDate: string;
          annualizationType: TAnnualizationType | null;
          reconciliation?: boolean;
      }
    | null;

export type ViewExportModalParams = {
    useState: () => {
        showModal: boolean;
        exportConfig: TExportConfig;
        exportData: TExportData;
        nonOperatingAccountsInCoa: boolean;
    };
    useCases: () => {
        updateExportConfig: ({
            exportConfigUpdate,
        }: {
            exportConfigUpdate: Partial<TExportConfig>;
        }) => void;
        onConfirm: ({
            nonOperatingAccountsInCoa,
        }: {
            nonOperatingAccountsInCoa: boolean;
        }) => Promise<void>;
        onCancel: () => void;
    };
};

export const createViewExportModal = ({
    useState,
    useCases,
}: ViewExportModalParams) => {
    const ViewExportModal: React.FC = () => {
        const {
            showModal,
            exportConfig: savedExportConfig,
            exportData,
            nonOperatingAccountsInCoa,
        } = useState();
        const isReconciliation =
            exportData?.tag === "annualization" &&
            exportData.reconciliation === true;
        const exportConfig = isReconciliation
            ? {
                  ...savedExportConfig,
                  exportFormat: "proda" as const,
                  prodaIncludeMappings: false,
              }
            : savedExportConfig;
        const { updateExportConfig, onConfirm, onCancel } = useCases();

        const changeExportFormat = (event: ChangeEvent<HTMLInputElement>) => {
            updateExportConfig({
                exportConfigUpdate: {
                    exportFormat: event.target
                        .value as TExportConfig["exportFormat"],
                },
            });
        };
        const toggleCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
            updateExportConfig({
                exportConfigUpdate: {
                    [event.target.value]: event.target.checked,
                },
            });
        };

        const extractionContainsNonExportable =
            exportData?.tag === "extractions" &&
            exportData.extractions.some(
                extraction => !canExportExtraction(extraction.extractionType),
            );

        const extractionContainsNonCompatExportable =
            (exportData?.tag === "annualization" &&
                exportData.annualizationType !== null &&
                exportData.annualizationType !== undefined &&
                (exportData.reconciliation === true ||
                    !canCompatExportAnnualizationType(
                        exportData.annualizationType,
                    ))) ||
            (exportData?.tag === "extractions" &&
                exportData.extractions.some(
                    extraction =>
                        !canCompatExportExtraction(extraction.extractionType),
                ));

        const hasErrors = !(
            exportConfig.exportFormat == "compat" ||
            (exportConfig.exportFormat == "proda" &&
                (exportConfig.prodaIncludeSummary ||
                    exportConfig.prodaIncludeDetailedSummary ||
                    exportConfig.prodaIncludeMappings))
        );
        const errorMessage = "Please select at least one export format.";
        let modalTitle;
        if (exportData?.tag === "extractions") {
            const nExtractions = exportData.extractions.filter(extraction =>
                canExportExtraction(extraction.extractionType),
            ).length;
            modalTitle =
                nExtractions === 1
                    ? "Export 1 extraction"
                    : `Export ${nExtractions} extractions`;
        } else if (exportData?.tag === "annualization") {
            const isBudget =
                exportData.annualizationType === "monthly_budget" ||
                exportData.annualizationType === "yearly_budget";
            modalTitle =
                "Export " +
                (exportData.reconciliation === true
                    ? "Reconciliation"
                    : isBudget
                      ? "Budget"
                      : "Operating Statement");
        }
        const description = `
          Operating Statements can be exported in multiple formats, please
          select from below the formats that best fit your analysis
          requirements.
        `;

        return (
            <Modal
                open={showModal}
                headerTitle={modalTitle}
                onCancel={onCancel}
                onClose={onCancel}
                onConfirm={() =>
                    void onConfirm({
                        nonOperatingAccountsInCoa,
                    })
                }
                textCancel="Cancel"
                textConfirm="Export"
                data-testid={testIds.modal}
            >
                <RadioGroup
                    name="select-export-format"
                    legend={description}
                    selectedValue={
                        extractionContainsNonCompatExportable
                            ? "proda"
                            : exportConfig.exportFormat
                    }
                    onChange={changeExportFormat}
                >
                    <Flex flexDirection="column">
                        <Box>
                            <RadioButton
                                id="export-format-proda"
                                label="PRODA Format"
                                value="proda"
                                mb={1}
                                data-testid={testIds.prodaRadioButton}
                            />
                            <CheckboxGroup
                                id="select-exports"
                                name="export"
                                hasErrors={hasErrors}
                                errorMessage={errorMessage}
                                ml={3}
                                mb={1}
                            >
                                <Flex flexDirection="column">
                                    <Checkbox
                                        id="includeDetailedSummary"
                                        label="Detailed operating statement"
                                        value="prodaIncludeDetailedSummary"
                                        checked={
                                            exportConfig.prodaIncludeDetailedSummary
                                        }
                                        onChange={toggleCheckbox}
                                        mb={1}
                                        data-testid={
                                            testIds.detailedSummaryCheckbox
                                        }
                                        disabled={
                                            exportConfig.exportFormat !==
                                            "proda"
                                        }
                                    />
                                    <Checkbox
                                        id="includeSummary"
                                        label="Summarized operating statement"
                                        value="prodaIncludeSummary"
                                        checked={
                                            exportConfig.prodaIncludeSummary
                                        }
                                        onChange={toggleCheckbox}
                                        mb={1}
                                        data-testid={testIds.summaryCheckbox}
                                        disabled={
                                            exportConfig.exportFormat !==
                                            "proda"
                                        }
                                    />
                                    {!isReconciliation && (
                                        <Checkbox
                                            id="includeMappings"
                                            label="Account mappings of original document"
                                            value="prodaIncludeMappings"
                                            checked={
                                                exportConfig.prodaIncludeMappings
                                            }
                                            onChange={toggleCheckbox}
                                            data-testid={
                                                testIds.mappingsCheckbox
                                            }
                                            disabled={
                                                exportConfig.exportFormat !==
                                                "proda"
                                            }
                                        />
                                    )}
                                </Flex>
                            </CheckboxGroup>
                        </Box>
                        <RadioButton
                            id="export-format-compat"
                            label={
                                exportData?.tag === "annualization" &&
                                exportData.reconciliation === true
                                    ? "Compatibility Format (not available for reconciliation)"
                                    : extractionContainsNonCompatExportable
                                      ? "Compatibility Format (only supported for monthly operating statements)"
                                      : "Compatibility Format"
                            }
                            value="compat"
                            mb={1}
                            data-testid={testIds.compatRadioButton}
                            disabled={extractionContainsNonCompatExportable}
                        />
                    </Flex>
                </RadioGroup>

                {nonOperatingAccountsInCoa && (
                    <>
                        <Box mb={1}>
                            <Typography
                                variant="body3"
                                sx={{ fontWeight: 700 }}
                            >
                                Additional Details:
                            </Typography>
                        </Box>
                        <CheckboxGroup
                            id="select-additional-options"
                            name="additional-options"
                            hasErrors={false}
                            ml={3}
                        >
                            <Flex flexDirection="column">
                                <Checkbox
                                    id="includeNonOperating"
                                    label="Include accounts below the line"
                                    value="includeNonOperating"
                                    checked={exportConfig.includeNonOperating}
                                    onChange={toggleCheckbox}
                                    mb={1}
                                    data-testid={testIds.nonOperatingCheckbox}
                                />
                            </Flex>
                        </CheckboxGroup>
                    </>
                )}
                {extractionContainsNonExportable && (
                    <WarningMessage
                        sx={{
                            padding: "10px",
                            marginY: "10px",
                        }}
                    >
                        <Box
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <Icon icon="triangle-exclamation" />
                            <Typography variant="body3">
                                Rent Rolls are unable to be exported here,
                                please do so from the relevant Rent Roll.
                            </Typography>
                        </Box>
                    </WarningMessage>
                )}
            </Modal>
        );
    };
    return ViewExportModal;
};
